import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { assetsApi, assetLogsApi } from '../../../api';
import { AssetLog } from '../../../types';
import { Card } from '../../../components/common/Card';
import { Icons } from '../../../components/common/Icons';
import { Button } from '../../../components/common/Button';
import { getGreeting, getFirstName } from '../../../utils/helpers';
import './ITAdminDashboard.css';

interface DashboardSummary {
  total: number;
  assigned: number;
  unassigned: number;
  available: number;
  under_maintenance: number;
  broken_retired: number;
  attention_needed: number;
  warranty_expiring_soon: number;
  by_state: Record<string, { count: number; color: string }>;
  by_category: Record<string, number>;
  category_list: Array<{ name: string; count: number; description?: string }>;
  by_location: Array<{ name: string; count: number; address?: string }>;
  recent_assignments: Array<{
    id: number;
    asset_code: string;
    asset_category: string;
    employee_name: string;
    assigned_at: string;
    assigned_by: string;
  }>;
}

export const ITAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recentLogs, setRecentLogs] = useState<AssetLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [summaryRes, logsRes] = await Promise.all([
        assetsApi.getSummary(),
        assetLogsApi.getRecentLogs(8),
      ]);
      
      setSummary(summaryRes as DashboardSummary);
      setRecentLogs(logsRes.logs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatShortDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short'
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created': return <Icons.Plus className="action-icon created" />;
      case 'updated': return <Icons.Edit className="action-icon updated" />;
      case 'deleted': return <Icons.Trash className="action-icon deleted" />;
      case 'assigned': return <Icons.UserPlus className="action-icon assigned" />;
      case 'returned': return <Icons.UserMinus className="action-icon returned" />;
      default: return <Icons.Activity className="action-icon" />;
    }
  };

  const getMaxCategoryCount = () => {
    if (!summary?.category_list?.length) return 1;
    return Math.max(...summary.category_list.map(c => c.count));
  };

  if (error) {
    return (
      <div className="dashboard-error">
        <Icons.AlertCircle />
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={fetchData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="it-admin-dashboard">
      {/* Header */}
      <header className="it-dashboard-header">
        <div className="it-dashboard-header-left">
          <h1>{getGreeting()}, {user ? getFirstName(user.name) : 'IT Admin'}!</h1>
          <p>IT Asset Management Dashboard</p>
        </div>
        <div className="it-dashboard-header-right">
          <Button variant="primary" onClick={() => navigate('/assets?action=create')}>
            <Icons.Plus /> Add Asset
          </Button>
          <Button variant="secondary" onClick={fetchData}>
            <Icons.Refresh /> Refresh
          </Button>
        </div>
      </header>

      {/* Primary Stats Row */}
      <section className="stats-grid stats-grid-5">
        <Card className="stat-card" padding="none" hoverable onClick={() => navigate('/assets')}>
          <div className="stat-card-inner">
            <div className="stat-icon-wrapper" style={{ backgroundColor: '#3b82f615', color: '#3b82f6' }}>
              <Icons.Laptop />
            </div>
            <div className="stat-details">
              <span className="stat-title">Total Assets</span>
              <span className="stat-value">{loading ? '-' : summary?.total || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="stat-card" padding="none" hoverable onClick={() => navigate('/assets?status=assigned')}>
          <div className="stat-card-inner">
            <div className="stat-icon-wrapper" style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}>
              <Icons.UserCheck />
            </div>
            <div className="stat-details">
              <span className="stat-title">Assigned</span>
              <span className="stat-value">{loading ? '-' : summary?.assigned || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="stat-card" padding="none" hoverable onClick={() => navigate('/assets?status=available')}>
          <div className="stat-card-inner">
            <div className="stat-icon-wrapper" style={{ backgroundColor: '#10b98115', color: '#10b981' }}>
              <Icons.Package />
            </div>
            <div className="stat-details">
              <span className="stat-title">Available</span>
              <span className="stat-value">{loading ? '-' : summary?.available || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="stat-card" padding="none" hoverable onClick={() => navigate('/assets?status=maintenance')}>
          <div className="stat-card-inner">
            <div className="stat-icon-wrapper" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
              <Icons.Tool />
            </div>
            <div className="stat-details">
              <span className="stat-title">Maintenance</span>
              <span className="stat-value">{loading ? '-' : summary?.under_maintenance || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="stat-card" padding="none" hoverable onClick={() => navigate('/assets?status=broken')}>
          <div className="stat-card-inner">
            <div className="stat-icon-wrapper" style={{ backgroundColor: '#ef444415', color: '#ef4444' }}>
              <Icons.AlertTriangle />
            </div>
            <div className="stat-details">
              <span className="stat-title">Broken/Retired</span>
              <span className="stat-value">{loading ? '-' : summary?.broken_retired || 0}</span>
            </div>
          </div>
        </Card>
      </section>

      {/* Alert Banners */}
      {!loading && (summary?.attention_needed || 0) + (summary?.warranty_expiring_soon || 0) > 0 && (
        <section className="alert-banner">
          {(summary?.attention_needed || 0) > 0 && (
            <div className="alert-item warning">
              <Icons.AlertCircle />
              <span><strong>{summary?.attention_needed}</strong> assets need attention</span>
            </div>
          )}
          {(summary?.warranty_expiring_soon || 0) > 0 && (
            <div className="alert-item info">
              <Icons.Clock />
              <span><strong>{summary?.warranty_expiring_soon}</strong> warranties expiring soon</span>
            </div>
          )}
        </section>
      )}

      {/* Grid Section */}
      <section className="dashboard-grid">
        {/* Category Distribution */}
        <Card className="category-card">
          <div className="card-header">
            <h3>Assets by Category</h3>
            <button className="text-btn" onClick={() => navigate('/assets/categories')}>
              Manage
            </button>
          </div>
          <div className="category-list">
            {loading ? (
              <div className="loading-placeholder">Loading...</div>
            ) : summary?.category_list?.length ? (
              summary.category_list.slice(0, 6).map((cat, idx) => (
                <div key={idx} className="category-row">
                  <div className="category-info">
                    <span className="category-name">{cat.name}</span>
                    <span className="category-count">{cat.count}</span>
                  </div>
                  <div className="category-bar-wrapper">
                    <div 
                      className="category-bar" 
                      style={{ width: `${Math.max((cat.count / getMaxCategoryCount()) * 100, 2)}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-placeholder">
                <Icons.Tag />
                <span>No categories defined</span>
              </div>
            )}
          </div>
        </Card>

        {/* Location Distribution */}
        <Card className="location-card">
          <div className="card-header">
            <h3>Assets by Location</h3>
            <button className="text-btn" onClick={() => navigate('/assets/locations')}>
              Manage
            </button>
          </div>
          <div className="location-list">
            {loading ? (
              <div className="loading-placeholder">Loading...</div>
            ) : summary?.by_location?.length ? (
              summary.by_location.slice(0, 5).map((loc, idx) => (
                <div key={idx} className="location-row">
                  <div className="location-icon">
                    <Icons.MapPin />
                  </div>
                  <div className="location-info">
                    <span className="location-name">{loc.name}</span>
                    {loc.address && <span className="location-address">{loc.address}</span>}
                  </div>
                  <span className="location-count">{loc.count}</span>
                </div>
              ))
            ) : (
              <div className="empty-placeholder">
                <Icons.MapPin />
                <span>No locations defined</span>
              </div>
            )}
          </div>
        </Card>

        {/* Recent Assignments */}
        <Card className="assignments-card">
          <div className="card-header">
            <h3>Recent Assignments</h3>
            <button className="text-btn" onClick={() => navigate('/assets/assignments')}>
              View All
            </button>
          </div>
          <div className="assignments-list">
            {loading ? (
              <div className="loading-placeholder">Loading...</div>
            ) : summary?.recent_assignments?.length ? (
              summary.recent_assignments.slice(0, 5).map((assignment) => (
                <div key={assignment.id} className="assignment-row">
                  <div className="assignment-icon">
                    <Icons.UserPlus />
                  </div>
                  <div className="assignment-info">
                    <div className="assignment-main">
                      <span className="asset-code">{assignment.asset_code}</span>
                      <Icons.ArrowRight className="assignment-arrow" />
                      <span className="employee-name">{assignment.employee_name}</span>
                    </div>
                    <span className="assignment-meta">
                      {assignment.asset_category} • {formatShortDate(assignment.assigned_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-placeholder">
                <Icons.UserPlus />
                <span>No recent assignments</span>
              </div>
            )}
          </div>
        </Card>

        {/* Activity Log */}
        <Card className="activity-card">
          <div className="card-header">
            <h3>Recent Activity</h3>
            <button className="text-btn" onClick={() => navigate('/assets/logs')}>
              View All
            </button>
          </div>
          <div className="activity-list">
            {loading ? (
              <div className="loading-placeholder">Loading...</div>
            ) : recentLogs.length ? (
              recentLogs.slice(0, 6).map((log) => (
                <div key={log.id} className="activity-row">
                  <div className="activity-icon-wrapper">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="activity-info">
                    <span className="activity-action">
                      <strong>{log.action}</strong> {log.asset_code || 'Asset'}
                    </span>
                    <span className="activity-meta">
                      {log.performer_name} • {formatShortDate(log.performed_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-placeholder">
                <Icons.Activity />
                <span>No recent activity</span>
              </div>
            )}
          </div>
        </Card>
      </section>

      {/* Quick Actions */}
      <section className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-row">
          <button className="quick-action-btn" onClick={() => navigate('/assets/new')}>
            <div className="action-icon blue"><Icons.Plus /></div>
            <span>Add Asset</span>
          </button>
          <button className="quick-action-btn" onClick={() => navigate('/assets/assignments/new')}>
            <div className="action-icon purple"><Icons.UserPlus /></div>
            <span>Assign Asset</span>
          </button>
          <button className="quick-action-btn" onClick={() => navigate('/assets/categories')}>
            <div className="action-icon green"><Icons.Tag /></div>
            <span>Categories</span>
          </button>
          <button className="quick-action-btn" onClick={() => navigate('/assets/states')}>
            <div className="action-icon orange"><Icons.Layers /></div>
            <span>States</span>
          </button>
          <button className="quick-action-btn" onClick={() => navigate('/assets/locations')}>
            <div className="action-icon teal"><Icons.MapPin /></div>
            <span>Locations</span>
          </button>
          <button className="quick-action-btn" onClick={() => navigate('/assets/logs')}>
            <div className="action-icon gray"><Icons.FileText /></div>
            <span>View Logs</span>
          </button>
        </div>
      </section>
    </div>
  );
};

export default ITAdminDashboard;
